import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { HelpTopicSummaryDto } from '@cpm/shared';
import { ArrowLeft, BookOpen, Search } from 'lucide-react';
import { HELP_ROUTE_BASE, useHelpTopic, useHelpTopics, type HelpShell } from '../api/help.js';
import { Markdown } from '../lib/markdown.js';
import { Card, EmptyState, ErrorNote, Input, PageHeader, Spinner } from './ui.js';

/**
 * The help center, shared between the panel (/help) and the portal
 * (/portal/help). Both shells render the same index + reader against their
 * own API base, so tenants only ever see client-audience topics.
 */

/** Inline cross-reference to a help topic, e.g. <HelpLink topic="backups/restore">. */
export function HelpLink({
  topic,
  shell = 'panel',
  children,
}: {
  /** `<moduleKey>/<slug>` of the target topic. */
  topic: string;
  shell?: HelpShell;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={`${HELP_ROUTE_BASE[shell]}/${topic}`}
      className="font-medium text-brand-600 hover:underline dark:text-brand-400"
    >
      {children}
    </Link>
  );
}

interface CategoryGroup {
  name: string;
  topics: HelpTopicSummaryDto[];
}

/** Group by category, keeping the API's (order, title) sort within and across groups. */
function groupByCategory(topics: HelpTopicSummaryDto[]): CategoryGroup[] {
  const groups = new Map<string, HelpTopicSummaryDto[]>();
  for (const topic of topics) {
    const list = groups.get(topic.category) ?? [];
    list.push(topic);
    groups.set(topic.category, list);
  }
  return [...groups.entries()]
    .map(([name, list]) => ({ name, topics: list }))
    .sort(
      (a, b) =>
        Math.min(...a.topics.map((t) => t.order)) - Math.min(...b.topics.map((t) => t.order)) ||
        a.name.localeCompare(b.name),
    );
}

export function HelpIndex({ shell }: { shell: HelpShell }) {
  const [query, setQuery] = useState('');
  // Debounce keystrokes so the server search fires on pauses, not every key.
  const [needle, setNeedle] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setNeedle(query), 250);
    return () => clearTimeout(handle);
  }, [query]);

  const topics = useHelpTopics(shell, needle);
  const groups = topics.data ? groupByCategory(topics.data) : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Help"
        description={
          shell === 'panel'
            ? 'Guides for every part of the panel — searchable, always in the app.'
            : 'Guides to your portal — searchable, right here in the app.'
        }
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute top-2.5 left-3 size-4 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help topics…"
          className="pl-9"
          aria-label="Search help topics"
        />
      </div>

      {topics.isLoading && <Spinner />}
      {topics.error != null && <ErrorNote error={topics.error} />}
      {topics.data && topics.data.length === 0 && (
        <EmptyState
          icon={<BookOpen className="size-10" />}
          title={needle ? 'No topics match your search' : 'No help topics available'}
          description={needle ? 'Try a different word — search covers titles, summaries and topic bodies.' : undefined}
        />
      )}

      {groups.map((group) => (
        <section key={group.name}>
          <h2 className="mb-2 text-sm font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
            {group.name}
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.topics.map((topic) => (
              <Link
                key={`${topic.moduleKey}/${topic.slug}`}
                to={`${HELP_ROUTE_BASE[shell]}/${topic.moduleKey}/${topic.slug}`}
                className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200 transition-shadow hover:shadow-md dark:bg-slate-900 dark:ring-slate-800"
              >
                <div className="flex items-center gap-2">
                  <BookOpen className="size-4 shrink-0 text-brand-600 dark:text-brand-400" />
                  <span className="font-medium">{topic.title}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                  {topic.summary}
                </p>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function HelpTopicView({ shell }: { shell: HelpShell }) {
  const { moduleKey = '', slug = '' } = useParams();
  const topic = useHelpTopic(shell, moduleKey, slug);
  const base = HELP_ROUTE_BASE[shell];

  return (
    <div className="space-y-4">
      <Link
        to={base}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
      >
        <ArrowLeft className="size-4" /> All help topics
      </Link>

      {topic.isLoading && <Spinner />}
      {topic.error != null && (
        <EmptyState
          icon={<BookOpen className="size-10" />}
          title="Topic not found"
          description="This topic may belong to a module that is currently disabled."
        />
      )}

      {topic.data && (
        <>
          <PageHeader title={topic.data.title} description={topic.data.summary} />
          <Card className="max-w-3xl px-5 py-4">
            <Markdown source={topic.data.body} basePath={base} hideFirstHeading />
          </Card>
        </>
      )}
    </div>
  );
}
